import { chakra } from '@chakra-ui/react';
import React from 'react';

import getCurrencyValue from 'lib/getCurrencyValue';
import { currencyUnits } from 'lib/units';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { Tooltip } from 'toolkit/chakra/tooltip';
import { WEI, WEI_IN_GWEI } from 'toolkit/utils/consts';

interface Props {
  value: string | null;
  currency?: string;
  exchangeRate?: string | null;
  className?: string;
  accuracy?: number;
  accuracyUsd?: number;
  decimals?: string | null;
  isLoading?: boolean;
  startElement?: React.ReactNode;
  showGweiTooltip?: boolean;
}

const CurrencyValue = ({ value, currency = '', decimals, exchangeRate, className, accuracy, accuracyUsd, isLoading, startElement, showGweiTooltip }: Props) => {
  if (isLoading) {
    return (
      <Skeleton className={ className } loading display="inline-block">0.00 ($0.00)</Skeleton>
    );
  }

  if (value === undefined || value === null) {
    return (
      <chakra.span className={ className }>
        -
      </chakra.span>
    );
  }
  const { valueCurr, valueStr: valueResult, usd: usdResult } = getCurrencyValue({ value, accuracy, accuracyUsd, exchangeRate, decimals });

  const valueElement = (
    <chakra.span display="inline-block" maxW="100%" whiteSpace="pre" overflow="hidden" textOverflow="ellipsis">
      { valueResult }{ currency ? ` ${ currency }` : '' }
    </chakra.span>
  );
  // [Basis MVP-NATIVE-1] `valueCurr` is denominated in coins, so reaching the
  // intermediate unit means multiplying up to base units and dividing back down
  // by that unit's scale. Upstream multiplied by GWEI alone, which is only
  // correct because Ethereum's 18 decimals happen to put gwei exactly halfway:
  // coins-per-gwei and wei-per-gwei are both 1e9 there. They are not equal on a
  // 9-decimal chain, where the shortcut would report LITHOS as if they were
  // tomos. This form yields the identical 1e9 factor on Ethereum.
  const valueInGwei = showGweiTooltip ? valueCurr.multipliedBy(WEI).dividedBy(WEI_IN_GWEI).toFormat() : null;

  return (
    <chakra.span className={ className } display="inline-flex" rowGap={ 3 } columnGap={ 1 }>
      { startElement }
      { showGweiTooltip ? (
        <Tooltip content={ `${ valueInGwei } ${ currencyUnits.gwei }` }>
          { valueElement }
        </Tooltip>
      ) : valueElement }
      { usdResult && <chakra.span color="text.secondary" fontWeight={ 400 }>(${ usdResult })</chakra.span> }
    </chakra.span>
  );
};

export default React.memo(chakra(CurrencyValue));
